// app/javascript/utils/firebase/appCheck.ts
//
// Firebase App Check, initialised lazily.
//
// App Check's reCAPTCHA v3 provider injects https://www.google.com/recaptcha/api.js,
// which pulls ~350 KB of recaptcha__<lang>.js plus an anchor iframe and a web worker.
// This module used to call initializeAppCheck() at import time, so any page that
// merely imported it (the hike page did, through TrailImportDialog) paid for
// reCAPTCHA during first paint even though nothing on the page asked for a token.
//
// Now nothing loads until the first caller actually needs a token. Callers that
// attach the header to a request use getAppCheckHeaders(); it never throws, and
// returns {} when a token can't be obtained so the request still goes out. The
// backend only logs a missing/invalid token (ApplicationController
// #verify_app_check_token), it never rejects the request.

import type { AppCheck } from 'firebase/app-check';

export const RECAPTCHA_SITE_KEY = '6LedPQEqAAAAADwAEbDFIdOONwyKpedA3vh3iqj4';
export const APP_CHECK_HEADER = 'X-Firebase-AppCheck';

let appCheckPromise: Promise<AppCheck> | null = null;

/**
 * Initialise App Check on first use and return the shared instance. Concurrent
 * callers share one initialisation; a failed one (e.g. the chunk didn't load)
 * is forgotten so the next caller can retry.
 */
export function getAppCheck(): Promise<AppCheck> {
  if (!appCheckPromise) {
    appCheckPromise = Promise.all([
      import('./initialise'),
      import('firebase/app-check'),
    ]).then(([{ app }, { initializeAppCheck, ReCaptchaV3Provider }]) => {
      const instance = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
      console.log('Firebase AppCheck initialized.');
      return instance;
    });
    appCheckPromise.catch(() => {
      appCheckPromise = null;
    });
  }
  return appCheckPromise;
}

/**
 * Headers carrying an App Check token for a request that wants one. Resolves
 * to {} (never rejects) if App Check can't produce a token.
 */
export async function getAppCheckHeaders(): Promise<Record<string, string>> {
  try {
    const appCheck = await getAppCheck();
    const { getToken } = await import('firebase/app-check');
    const { token } = await getToken(appCheck, false);
    return token ? { [APP_CHECK_HEADER]: token } : {};
  } catch (error) {
    console.error('[AppCheck] Could not get an App Check token:', error);
    return {};
  }
}

/** Test-only: forget the memoised instance. */
export function resetAppCheckForTests(): void {
  appCheckPromise = null;
}
